import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

const REQUIRED_PHRASE = "SUPPRIMER";

/**
 * Two-step destructive confirmation: user must type exactly "SUPPRIMER" in an
 * input before the confirm button enables. Used everywhere a hard delete is
 * triggered (alerts + users + empty-trash).
 */
export default function HardDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Supprimer définitivement",
  cancelLabel = "Annuler",
  onConfirm,
  loading = false,
  testId,
}) {
  const [phrase, setPhrase] = useState("");

  // Reset typed phrase whenever the dialog (re-)opens
  useEffect(() => {
    if (open) setPhrase("");
  }, [open]);

  const ready = phrase.trim() === REQUIRED_PHRASE && !loading;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid={testId}
        className="max-w-[440px] rounded-[20px]"
      >
        <AlertDialogHeader>
          <div className="mb-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600">
            <AlertTriangle size={18} strokeWidth={2.4} />
          </div>
          <AlertDialogTitle className="text-[18px] font-bold text-red-700">
            {title}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription
              data-testid={testId ? `${testId}-description` : undefined}
              className="text-[13.5px] text-slate-600 leading-snug whitespace-pre-line"
            >
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>

        <div className="rounded-[14px] border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] font-semibold leading-snug text-red-800">
          Cette action est{" "}
          <span className="underline decoration-red-400 underline-offset-2">
            irréversible
          </span>
          . La donnée sera supprimée de la base et ne pourra pas être restaurée.
        </div>

        <label className="mt-2 block text-[12.5px] font-semibold text-slate-700">
          Tapez{" "}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] text-red-700">
            {REQUIRED_PHRASE}
          </code>{" "}
          pour confirmer
        </label>
        <input
          type="text"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder={REQUIRED_PHRASE}
          data-testid={testId ? `${testId}-input` : undefined}
          className="mt-1 w-full rounded-[12px] border border-slate-300 bg-white px-3 py-2.5 font-mono text-[14px] tracking-wide text-slate-900 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-200"
        />

        <AlertDialogFooter className="mt-3 flex gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid={testId ? `${testId}-cancel` : undefined}
            className="flex-1 rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!ready}
            data-testid={testId ? `${testId}-confirm` : undefined}
            className="flex-1 rounded-[14px] bg-red-600 px-5 py-3 text-[14px] font-bold text-white shadow-[0_4px_18px_-2px_rgba(239,68,68,0.55)] transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 disabled:shadow-none"
          >
            {loading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Patientez…
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
